import type { InputHTMLAttributes, ButtonHTMLAttributes, PropsWithChildren, ReactNode } from "react";
import {
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Search,
  X,
} from "lucide-react";
import type { PageMeta } from "../types";
import { formatNumber, formatNumberWhileTyping, normalizeNumber } from "../numbers";

export function Button({
  children,
  className = "",
  variant = "primary",
  loading = false,
  ...props
}: PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>> & {
  variant?: "primary" | "success" | "outline" | "danger" | "ghost";
  loading?: boolean;
}) {
  return (
    <button className={`button ${variant} ${className}`} disabled={loading || props.disabled} {...props}>
      {loading && <LoaderCircle size={15} className="spin" />}
      {children}
    </button>
  );
}

export function Panel({
  children,
  className = "",
  title,
  actions,
}: PropsWithChildren<{
  className?: string;
  title?: ReactNode;
  actions?: ReactNode;
}>) {
  return (
    <section className={`panel ${className}`}>
      {title && (
        <div className="panel-heading">
          <h2>{title}</h2>
          <div className="heading-actions">{actions}</div>
        </div>
      )}
      {children}
    </section>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder = "Tìm kiếm...",
}: {
  value: string;
  onChange(value: string): void;
  placeholder?: string;
}) {
  return (
    <label className="search-box">
      <Search size={18} />
      <input
        aria-label={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      <span>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Xóa tìm kiếm"
          >
            <X size={15} />
          </button>
        )}
      </span>
    </label>
  );
}

export function Pagination({
  meta,
  onPage,
}: {
  meta?: PageMeta;
  onPage(page: number): void;
}) {
  if (!meta || meta.totalPages <= 1)
    return (
      <div className="pagination-summary">
        Hiển thị {formatNumber(meta?.total ?? 0)} bản ghi
      </div>
    );
  const pages = paginationItems(meta.page, meta.totalPages);
  return (
    <div className="pagination">
      <span>Hiển thị {formatNumber(meta.total)} bản ghi</span>
      <button aria-label="Trang trước" disabled={meta.page === 1} onClick={() => onPage(meta.page - 1)}>
        <ChevronLeft size={17} />
      </button>
      {pages.map((item, index) => item === "…" ? (
        <span className="pagination-ellipsis" aria-hidden="true" key={`ellipsis-${index}`}>…</span>
      ) : (
        <button
          aria-current={item === meta.page ? "page" : undefined}
          aria-label={`Trang ${item}`}
          key={item}
          className={item === meta.page ? "active" : ""}
          onClick={() => onPage(item)}
        >
          {item}
        </button>
      ))}
      <button
        aria-label="Trang sau"
        disabled={meta.page === meta.totalPages}
        onClick={() => onPage(meta.page + 1)}
      >
        <ChevronRight size={17} />
      </button>
    </div>
  );
}

function paginationItems(currentPage: number, totalPages: number): Array<number | "…"> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const middle = currentPage <= 4
    ? [2, 3, 4, 5]
    : currentPage >= totalPages - 3
      ? [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1]
      : [currentPage - 1, currentPage, currentPage + 1];
  const numberedPages = [1, ...middle, totalPages];
  return numberedPages.reduce<Array<number | "…">>((items, page) => {
    const previous = items.at(-1);
    if (typeof previous === "number" && page - previous > 1) items.push("…");
    if (previous !== page) items.push(page);
    return items;
  }, []);
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-mark">+</div>
      <h3>{title}</h3>
      <p>{detail}</p>
      {action}
    </div>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  hideHeader = false,
}: PropsWithChildren<{
  open: boolean;
  title: string;
  onClose(): void;
  hideHeader?: boolean;
}>) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={hideHeader ? undefined : "modal-title"}
        aria-label={hideHeader ? title : undefined}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {!hideHeader && (
          <header>
            <h2 id="modal-title">{title}</h2>
            <button onClick={onClose} aria-label="Đóng">
              <X />
            </button>
          </header>
        )}
        {children}
      </section>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Xác nhận",
  variant = "danger",
  pending = false,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  variant?: "primary" | "danger";
  pending?: boolean;
  onClose(): void;
  onConfirm(): void;
}) {
  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div className="confirm-dialog">
        <p>{message}</p>
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
            Hủy
          </Button>
          <Button type="button" variant={variant} onClick={onConfirm} disabled={pending}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: "Bản nháp",
    in_review: "Chờ duyệt",
    approved_scheduled: "Đã duyệt (lịch)",
    active: "Đang áp dụng",
    superseded: "Hết hiệu lực",
    completed: "Hoàn tất",
    processing: "Đang xử lý",
    uploading: "Đang tải",
    validating: "Đang kiểm tra",
    ready: "Sẵn sàng lưu",
    failed: "Cần xử lý lại",
  };
  return labels[status] ?? "Đang cập nhật";
}

export function StatusBadge({ status }: { status: string }) {
  return <span className={`status ${status}`}>{statusLabel(status)}</span>;
}

export function FormField({
  label,
  children,
  help,
  className = "",
}: PropsWithChildren<{ label: string; help?: string; className?: string }>) {
  return (
    <label className={`form-field ${className}`}>
      <span>{label}</span>
      {children}
      {help && <small>{help}</small>}
    </label>
  );
}

/** Ô nhập số theo định dạng Việt Nam, đồng thời vẫn gửi giá trị chuẩn cho biểu mẫu. */
export function NumericInput({
  value,
  onValueChange,
  allowNegative = false,
  className = "",
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & {
  value: string | number;
  onValueChange(value: string): void;
  allowNegative?: boolean;
}) {
  return (
    <input
      {...props}
      type="text"
      inputMode="decimal"
      className={`numeric-input ${className}`}
      value={formatNumberWhileTyping(String(value ?? ""), { allowNegative })}
      onChange={(event) => {
        const formatted = formatNumberWhileTyping(event.target.value, { allowNegative });
        onValueChange(normalizeNumber(formatted, { allowNegative }) ?? formatted);
      }}
    />
  );
}

/** Dùng ở vị trí đủ rộng: mã là thông tin phụ, tên là thông tin người dùng nhận biết. */
export function CatalogLabel({ code, name }: { code: string; name: string }) {
  return <span className="catalog-label"><span className="catalog-code">{code}</span><span className="catalog-name">{name}</span></span>;
}
